import serviceProvider from '@peoplenet/vi-components-ui/src/middleware/GeotabServiceProvider';

const GeotabAPI = require('mg-api-js');

export const GEOTAB_SYSTEM_ID = 'geotab';

/**
 * This function is used to do the calls using Geotab API
 * @param {string} method - The HTTP method that will be used (Get, Post)
 * @param {object} params - Object with the syntax from Geotab API documentation
 * (https://geotab.github.io/sdk/software/api/reference/)
 * @returns {object} The response from Geotab API with the object requested in the params
 */
export async function geotabService(method, params) {
  const { geotabApi } = window;

  try {
    return await geotabApi.call(method, params);
  } catch (error) {
    throw new Error(error);
  }
}

/**
 * This function is used to create a new instance of Geotab API and set into the global variable 'window' to be used for all the calls for Geotab.
 * We get credentials from global variable windows.geotabSession - That is set up in the first connection to Geotab Portal.
 */
export function setGeotabAPI() {
  const { userName, sessionId, database, server } = window.geotabSession;

  if (userName && sessionId && database && server) {
    const authentication = {
      credentials: {
        userName,
        sessionId,
        database,
      },
      path: server,
    };
    const api = new GeotabAPI(authentication);
    window.geotabApi = api;
  }
}

/**
 * This is a function for development/testing purpose
 * @returns {object} The credentials (database, username, password, server) from .env file to authenticate in Geotab API
 */
function getAuthCredentials() {
  return {
    credentials: {
      database: process.env.REACT_APP_GEOTAB_DATABASE,
      userName: process.env.REACT_APP_GEOTAB_USERNAME,
      password: process.env.REACT_APP_GEOTAB_PASSWORD,
    },
    path: process.env.REACT_APP_GEOTAB_SERVER,
  };
}

/**
 * This is a function for development/testing purpose
 * @returns {string} The sessionId to facilitate the authentication in Geotab API (It will be present in the request headers)
 */
async function getSessionId() {
  const credentials = localStorage.getItem('geotabAPI_credentials');

  if (!credentials) {
    const authentication = getAuthCredentials();
    const api = new GeotabAPI(authentication);

    return await api.authenticate().then((success) => {
      return success.credentials.sessionId;
    });
  }

  const { sessionId } = JSON.parse(credentials);
  return sessionId;
}

/**
 * This is a function for development/testing purpose
 * @global We do not have the credentials from Geotab Portal to run it locally, so we get the credentials from .env file and the sessionId from other function and set them into the global variable 'window'. Thus, we are able to run locally as if we had been running inside the Geotab Portal
 */
export async function setGeotabCredentials() {
  const sessionId = await getSessionId();
  const userName = process.env.REACT_APP_GEOTAB_USERNAME;
  const database = process.env.REACT_APP_GEOTAB_DATABASE;
  const server = process.env.REACT_APP_GEOTAB_SERVER;

  window.geotabSession = {
    userName,
    sessionId,
    database,
    server,
  };
}

function setGeotabService() {
  localStorage.setItem('systemId', GEOTAB_SYSTEM_ID);
  if (localStorage.getItem('geotabDefaultLanguage')) {
    localStorage.setItem(
      'language',
      localStorage.getItem('geotabDefaultLanguage'),
    );
  }
  serviceProvider.setService({
    setGeotabAPI,
    setGeotabCredentials,
    geotabService,
  });
}

export default setGeotabService();
